import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import type { Permiso } from '@tax/shared';
import { ToastProvider, routeTransition } from '@tax/ui';
import { AppShell } from './app/AppShell';
import { AuthGate } from './app/AuthGate';
import { startLive } from './live/store';
import { usePuede, useSesion } from './api/queries';
import { Operacion } from './pages/Operacion';
import { Carreras } from './pages/Carreras';
import { Flota } from './pages/Flota';
import { Zonas } from './pages/Zonas';
import { Reportes } from './pages/Reportes';
import { Sistema } from './pages/Sistema';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Un 401 o un 403 no se reintentan: no van a mejorar solos y cada
      // reintento retrasa el mensaje que el operador necesita ver.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * Las rutas se cruzan dentro del marco: la barra lateral y la superior no se
 * vuelven a montar, así la navegación no parpadea ni pierde el scroll lateral.
 */
function AnimatedRoutes() {
  const location = useLocation();
  /*
   * La transición es por sección, no por dirección: `/carreras` y
   * `/carreras/<id>` son la misma página con una ficha abierta. Con la
   * dirección entera como clave, abrir una carrera desmontaba la página y el
   * filtro volvía a «Activas» por debajo de la ficha.
   */
  const seccion = `/${location.pathname.split('/')[1] ?? ''}`;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={seccion}
        variants={routeTransition}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="h-full"
      >
        <Routes location={location}>
          <Route path="/" element={<Operacion />} />
          <Route path="/carreras" element={<Carreras />} />
          <Route path="/carreras/:rideId" element={<Carreras />} />
          <Route path="/flota" element={<Flota />} />
          <Route path="/zonas" element={<Zonas />} />
          <Route
            path="/reportes"
            element={
              <ConPermiso permiso="reportes.ver">
                <Reportes />
              </ConPermiso>
            }
          />
          <Route path="/sistema" element={<Sistema />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Una página entera que pide un permiso: sin él, a Operación. Es para quien
 * llega por un enlace guardado; la barra lateral ya no la enseña.
 */
function ConPermiso({ permiso, children }: { permiso: Permiso; children: ReactNode }) {
  const sesion = useSesion();
  const puede = usePuede();
  if (sesion.isLoading) return null;
  return puede(permiso) ? children : <Navigate to="/" replace />;
}

/** El estado en vivo arranca solo cuando hay sesión: sin ella, todo da 401. */
function LiveWorkspace({ userName }: { userName: string }) {
  useEffect(() => startLive(), []);

  return (
    <BrowserRouter>
      <AppShell userName={userName}>
        <AnimatedRoutes />
      </AppShell>
    </BrowserRouter>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* `reducedMotion="user"` honra la preferencia del sistema en toda la app. */}
      <MotionConfig reducedMotion="user">
        <ToastProvider>
          <AuthGate>{(user) => <LiveWorkspace userName={user.name} />}</AuthGate>
        </ToastProvider>
      </MotionConfig>
    </QueryClientProvider>
  );
}
